<template>
    <div :class="wrapperClasses" :style="wrapperStyles">
        <div class="label-desc">
            <div class="label" :for="name" v-if="label" :style="labelStyles">
                <span class="inner">{{ label }}</span>
                <span class="required" v-if="required">*</span>
            </div>
            <div
                class="extra-info"
                v-if="extraInfo"
                @click="$emit('extra-info-click')"
                :style="extraInfoStyles"
            >
                <i class="far fa-info-circle"></i>
                <span v-if="typeof extraInfo === 'string'" class="extra-info-title">{{ extraInfo }}</span>
            </div>
            <div class="description" v-if="description">{{ description }}</div>
        </div>
        <div class="input-wrapper">
            <div v-if="prefix" class="fixed group-left">{{ prefix }}</div>
            <textarea
                type="text"
                :class="classes"
                :ref="name"
                :name="name"
                :id="id"
                :style="{
                    ...inputStyles,
                    resize
                }"
                :disabled="disabled"
                :readonly="readonly"
                :value="value"
                @input="onInputMask"
                @change="onChangeTrim"
                @keydown="onKeydown"
                @focus="onFocus"
                @blur="onBlur"
                v-imask="imask"
                @accept="onAccept"
                @complete="onComplete"
            />
            <div v-if="suffix" class="fixed group-right">{{ suffix }}</div>
        </div>
    </div>
</template>

<script>
    import Autofocus from '../mixins/Autofocus.vue';
    import FormControl from '../mixins/FormControl.vue';
    import { IMaskDirective } from 'vue-imask';

    export default {
        name: 'FormTextArea',
        data() {
            return {};
        },
        mixins: [Autofocus, FormControl],
        props: {
            imask: Object,
            prefix: [String, Number],
            suffix: [String, Number],
            inputClasses: {
                type: Object,
                default: () => {
                    return {};
                },
            },
            // Height the textarea initially starts with before it is resized
            defaultHeight: {
                type: String,
                default: "200px",
            },
            resize: {
                type: String,
                default: 'vertical'
            },
            trim: {
                type: Boolean,
                default: false
            },
        },
        computed: {
            classes() {
                return {
                    'group-right': !!this.prefix,
                    'group-left': !!this.suffix,
                    'has-prefix-suffix': this.prefix || this.suffix,
                    ...this.inputClasses,
                };
            },
        },
        directives: {
            imask: IMaskDirective,
        },
        methods: {
            async onInputMask(e) {
                if (!this.imask) {
                    this.onInput(e);
                }
            },
            onAccept(e) {
                this.onInput(e);
                this.$emit('accept', e.detail);
            },
            onComplete(e) {
                this.$emit('complete', e.detail);
            },
            onChangeTrim(e) {
                if (this.trim) {
                    let trimmed = e.target.value.trim();
                    if (e.target.value !== trimmed) {
                        e.target.value = trimmed;
                        this.onInput(e);
                    }
                }
                this.onChange(e);
            }
        },
        mounted() {
            // Don't set textarea height if we're setting a static height on the wrapper
            if (this.height) return
            this.$refs[this.name].style.height = this.defaultHeight;
        },
    };
</script>

<style lang="scss" scoped>
    @import '../scss/formControl';

    .control-wrapper {
        .label {
            transform: translate3d($padding-left, 2em, 0.01px);
            color: $color-inactive-text;
            pointer-events: none;
        }

        &.filled .label,
        &:focus-within .label {
            transform: translate3d(0, 0, 0.01px);
            color: $color-label;
        }
    }

    textarea {
        flex-grow: 1;
        height: 100%;
        font-size: 100%;
        padding-left: $padding-left;

        &.has-prefix-suffix {
            min-width: 0;
        }
    }

    .input-wrapper {
        display: flex;
        height: 100%;

        .group-right {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-left: 0;
        }

        .group-left {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }
    }

    .fixed {
        background-color: $color-disabled-bg;
        color: $color-disabled-text;
        @include border;
        @include border-radius-small;
        display: flex;
        align-items: center;
        padding: 0 0.6em;
    }
</style>